/*
 * Song & Timing workspace, latency calibration and the wizard Beat step (W2-08 timing).
 * Design tokens only (src/ui/styles/tokens.css); state is never shown by colour alone (icons and words go with
 * it). Layouts hold at 1280x600: the left column scrolls, the main column splits overview / bar-by-bar.
 */

/* ---------------------------------------------------------------- panels */

.panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  background: var(--color-bg);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-2) var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.mainPanel {
  display: grid;
  grid-template-rows: minmax(120px, 34%) minmax(0, 1fr);
  height: 100%;
  min-height: 0;
  background: var(--color-bg);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.status {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
  background: var(--color-surface-1);
  min-height: calc(28px * var(--ui-scale));
}

.status[data-kind='error'] {
  background: var(--color-danger-muted);
}

.status[data-kind='ok'] {
  background: var(--color-success-muted);
}

.status[data-kind='warning'] {
  background: var(--color-warning-muted);
}

.grow {
  flex: 1 1 auto;
  min-width: 0;
}

.row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.rowTight {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.muted {
  color: var(--color-text-muted);
}

.subtle {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.help {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-normal);
  margin: 0;
}

.inlineNote {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-normal);
  margin: 0;
}

.inlineNote > svg {
  flex: none;
}

.sentence {
  font-size: var(--text-md);
  font-weight: var(--weight-medium);
  margin: 0;
}

.notice {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
  padding: var(--space-2);
  border-radius: var(--radius-md);
  background: var(--color-info-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-normal);
}

.notice[data-kind='warning'] {
  background: var(--color-warning-muted);
}

.notice[data-kind='error'] {
  background: var(--color-danger-muted);
}

.notice[data-kind='ok'] {
  background: var(--color-success-muted);
}

.mono {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

/* ---------------------------------------------------------------- inputs */

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.label {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  font-weight: var(--weight-medium);
}

.input {
  height: var(--control-h-sm);
  min-width: 0;
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

.input:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.input[aria-invalid='true'] {
  border-color: var(--color-danger);
}

.bpmInput {
  width: calc(96px * var(--ui-scale));
  height: var(--control-h-md);
  font-size: var(--text-lg);
}

.cellInput {
  width: 100%;
}

.error {
  color: var(--color-danger);
  font-size: var(--text-xs);
  display: flex;
  gap: var(--space-1);
  align-items: center;
}

/* ---------------------------------------------------------------- candidates and tap */

.candidates {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.candidate {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-surface-1);
}

.candidate[data-current='true'] {
  background: var(--color-accent-muted);
}

.strength {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.meter {
  display: inline-block;
  width: calc(36px * var(--ui-scale));
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-3);
  overflow: hidden;
}

.meterFill {
  display: block;
  height: 100%;
  background: var(--color-accent);
}

.tapButton {
  min-width: calc(88px * var(--ui-scale));
}

.tapReading {
  font-variant-numeric: tabular-nums;
}

.tapDots {
  display: inline-flex;
  gap: 3px;
}

.tapDot {
  width: 6px;
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-3);
}

.tapDot[data-on='true'] {
  background: var(--color-accent);
}

/* ---------------------------------------------------------------- tempo table */

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.table th {
  text-align: left;
  font-weight: var(--weight-medium);
  color: var(--color-text-muted);
  padding: var(--space-1);
  border-bottom: var(--border-width) solid var(--color-divider);
}

.table td {
  padding: 2px var(--space-1);
  vertical-align: middle;
  border-bottom: var(--border-width) solid var(--color-divider);
}

.table tr[data-current='true'] td {
  background: var(--color-surface-hover);
}

/* Inherited values (a row that does not set them) show as placeholders. */
.input::placeholder {
  color: var(--color-text-subtle);
  font-style: italic;
  opacity: 1;
}

.actionsCell {
  width: 1%;
  white-space: nowrap;
}

.popoverForm {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2);
  min-width: calc(220px * var(--ui-scale));
}

/* ---------------------------------------------------------------- canvases */

.canvasBox {
  position: relative;
  /* Fill the panel column (song overview) or the wizard box; without it the canvas host got 0 px (UX-068). */
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  border-bottom: var(--border-width) solid var(--color-divider);
  display: flex;
  flex-direction: column;
}

.canvasHost {
  position: relative;
  flex: 1 1 auto;
  min-height: calc(64px * var(--ui-scale));
  outline: none;
}

.canvasHost:focus-visible {
  box-shadow: inset 0 0 0 var(--focus-ring-width) var(--color-focus-ring);
}

.canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
}

.canvas[data-cursor='grab'] {
  cursor: grab;
}

.canvas[data-cursor='grabbing'] {
  cursor: grabbing;
}

.canvas[data-cursor='pointer'] {
  cursor: pointer;
}

.canvas[data-cursor='crosshair'] {
  cursor: crosshair;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
  padding: var(--space-1) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
  background: var(--color-surface-1);
}

.toolbarTitle {
  font-weight: var(--weight-semibold);
  font-size: var(--text-sm);
}

.readout {
  position: absolute;
  top: var(--space-1);
  right: var(--space-2);
  padding: 0 var(--space-1);
  border-radius: var(--radius-xs);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  pointer-events: none;
}

.overlayMessage {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: var(--color-text-muted);
}

/* ---------------------------------------------------------------- check by ear */

.bottom {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  align-content: flex-start;
  background: var(--color-surface-1);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.question {
  font-weight: var(--weight-medium);
}

.answers {
  display: inline-flex;
  gap: var(--space-1);
}

.divider {
  width: var(--border-width);
  align-self: stretch;
  background: var(--color-divider);
}

/* ---------------------------------------------------------------- calibration */

.calBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-height: calc(180px * var(--ui-scale));
}

.calTarget {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: calc(120px * var(--ui-scale));
  border: var(--border-width) dashed var(--color-border-strong);
  border-radius: var(--radius-lg);
  background: var(--color-surface-sunken);
}

.calDots {
  display: flex;
  gap: var(--space-1);
  flex-wrap: wrap;
  justify-content: center;
}

.calDot {
  width: 10px;
  height: 10px;
  border-radius: var(--radius-pill);
  border: var(--border-width) solid var(--color-border-strong);
}

.calDot[data-countin='true'] {
  border-style: dashed;
}

.calDot[data-done='true'] {
  background: var(--color-accent);
  border-color: var(--color-accent);
}

.calResult {
  font-size: var(--text-xl);
  font-weight: var(--weight-semibold);
  font-variant-numeric: tabular-nums;
}

/* ---------------------------------------------------------------- banner */

.banner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: calc(32px * var(--ui-scale));
  padding: var(--space-1) var(--space-4);
  border-bottom: var(--border-width) solid var(--color-border);
  background: var(--color-warning-muted);
  font-size: var(--text-sm);
}

/* ---------------------------------------------------------------- A/V sync lab (#/dev/av) */

.lab {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  height: 100%;
  overflow: auto;
  padding: var(--space-4) var(--space-5);
  background: var(--color-bg);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.labHeader {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.labTitle {
  flex: 1;
  margin: 0;
  font-size: var(--text-lg);
}

.labSection {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-1);
}

.labH2 {
  margin: 0;
  font-size: var(--text-md);
}

.labSquare {
  width: calc(160px * var(--ui-scale));
  height: calc(160px * var(--ui-scale));
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
}

.labSquare[data-on='true'] {
  background: var(--color-text);
}

/* ---------------------------------------------------------------- wizard step */

.step {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-height: 0;
}

.stepOverview {
  position: relative;
  height: calc(140px * var(--ui-scale));
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.stepColumns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-4);
}

@media (max-width: 900px) {
  .stepColumns {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (prefers-reduced-motion: reduce) {
  .meterFill {
    transition: none;
  }
}
